import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, Info, Loader } from "lucide-react";
import { toast } from "react-toastify";
import {
  TEMPLATE_SUBTYPES,
  VARIABLE_TYPES,
  MEDIA_SAMPLES,
  INITIAL_TEMPLATE_STATE,
  TC_RADIUS,
} from "../constants/templateCreatorConstants";
import { TEMPLATE_LANGUAGES } from "../constants/templateLanguages";
import TemplateLanguageSelect from "../components/TemplateLanguageSelect";
import {
  validateTemplateName,
  validateBody,
  validateFooter,
  validateHeaderText,
  validateVariableSamples,
  validateCtaButtons,
} from "../utils/templateCreatorValidation";
import { buildTemplateJSON } from "../utils/templateCreatorPayload";
import { createTemplate } from "../apis/template";
import { transformDuplicateTemplateToCreatorState } from "../utils/templateDataTransformers";
import BodyEditor from "../components/templateCreator/BodyEditor";
import HeaderEditor from "../components/templateCreator/HeaderEditor";
import FooterEditor from "../components/templateCreator/FooterEditor";
import ButtonsBuilder from "../components/templateCreator/ButtonsBuilder";
import AuthenticationForm from "../components/templateCreator/AuthenticationForm";
import TemplatePreview from "../components/templateCreator/TemplatePreview";
import SetupTemplateForm from "../components/templateCreator/SetupTemplateForm";
import PreApprovedTemplatesBanner from "../components/templateCreator/PreApprovedTemplatesBanner";
import SetupTemplatePreview, {
  SETUP_CATEGORY_PREVIEWS,
} from "../components/templateCreator/SetupTemplatePreview";
import FormFieldError from "../components/templateCreator/FormFieldError";
import TemplateCreatorStepBar, {
  TemplateSummaryBanner,
} from "../components/templateCreator/TemplateCreatorStepBar";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";

const shortLanguageLabel = (label) => label.split(" (")[0];

const getLanguageDisplay = (code) => {
  const label =
    TEMPLATE_LANGUAGES.find((l) => l.code === code)?.label || code;
  return shortLanguageLabel(label);
};

const getSubtypeLabel = (category, subtype) =>
  TEMPLATE_SUBTYPES[category]?.find((s) => s.value === subtype)?.label ||
  subtype;

const TemplateCreator = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedPhoneAccount } = usePhoneAccount();
  const [step, setStep] = useState(1);
  const [state, setState] = useState({ ...INITIAL_TEMPLATE_STATE });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    document.documentElement.setAttribute("data-ping-collapse-nav", "");
    return () => document.documentElement.removeAttribute("data-ping-collapse-nav");
  }, []);

  // Prefill form when duplicating an existing template from Templates list
  useEffect(() => {
    const duplicateData = location.state?.duplicateTemplate;
    if (!duplicateData) return;

    const transformed = transformDuplicateTemplateToCreatorState(duplicateData);
    setState((prev) => ({
      ...prev,
      ...transformed,
    }));
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const update = useCallback((field, value) => {
    setState((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "category") {
        next.subtype =
          value === "AUTHENTICATION" ? "one_time_passcode" : "default";
      }
      if (field === "mediaSample" && value !== "none") {
        next.headerText = "";
        next.headerVariables = [];
        next.headerFile = null;
        next.headerFilePreview = null;
      }
      return next;
    });
    if (field === "name") {
      const err = validateTemplateName(value);
      setErrors((prev) => ({ ...prev, name: err || "" }));
    }
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MEDIA_RULES = {
      image: {
        maxSize: 5 * 1024 * 1024,
        maxLabel: "5 MB",
        allowedTypes: ["image/jpeg", "image/jpg", "image/png"],
        allowedLabel: "JPG, JPEG, PNG",
      },
      video: {
        maxSize: 16 * 1024 * 1024,
        maxLabel: "16 MB",
        allowedTypes: ["video/mp4", "video/3gpp"],
        allowedLabel: "MP4, 3GP",
      },
      document: {
        maxSize: 100 * 1024 * 1024,
        maxLabel: "100 MB",
        allowedTypes: [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.ms-powerpoint",
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "text/plain",
        ],
        allowedLabel: "PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT",
      },
    };

    const rule = MEDIA_RULES[state.mediaSample];
    if (rule) {
      const ext = file.name.split(".").pop().toLowerCase();
      const extMap = {
        image: ["jpg", "jpeg", "png"],
        video: ["mp4", "3gp"],
        document: ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx", "txt"],
      };
      const allowedExts = extMap[state.mediaSample] || [];
      const typeMatch =
        rule.allowedTypes.includes(file.type) || allowedExts.includes(ext);

      if (!typeMatch) {
        toast.error(
          `Invalid file type. Allowed: ${rule.allowedLabel}`
        );
        e.target.value = "";
        return;
      }
      if (file.size > rule.maxSize) {
        toast.error(
          `File too large. Maximum size: ${rule.maxLabel}`
        );
        e.target.value = "";
        return;
      }
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setState((prev) => ({
        ...prev,
        headerFile: file,
        headerFilePreview: ev.target.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleFileRemove = () => {
    setState((prev) => ({
      ...prev,
      headerFile: null,
      headerFilePreview: null,
      headerMediaUrl: null,
    }));
  };

  const runValidation = () => {
    const nameErr = validateTemplateName(state.name);
    if (nameErr) return nameErr;

    if (state.category !== "AUTHENTICATION") {
      const headerErr = validateHeaderText(state.headerText, state.headerVariables);
      if (headerErr) return headerErr;

      const bodyErr = validateBody(state.body, state.variableType);
      if (bodyErr) return bodyErr;

      const sampleErr = validateVariableSamples(
        [...(state.bodyVariables || []), ...(state.headerVariables || [])]
      );
      if (sampleErr) return sampleErr;

      const footerErr = validateFooter(state.footer);
      if (footerErr) return footerErr;

      const ctaErr = validateCtaButtons(state.ctaButtons, state.variableType);
      if (ctaErr) return ctaErr;
    }

    return null;
  };

  const handleSubmit = async () => {
    if (!selectedPhoneAccount?.wabaId || !selectedPhoneAccount?.phoneId) {
      toast.error("Please select a WhatsApp Business Account first");
      return;
    }

    const validationError = runValidation();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = buildTemplateJSON(state);
      const formData = new FormData();
      formData.append("templateData", JSON.stringify(payload));
      formData.append("wabaId", selectedPhoneAccount.wabaId);
      formData.append("phoneId", selectedPhoneAccount.phoneId);
      if (state.headerMediaUrl) {
        formData.append("header_media_url", state.headerMediaUrl);
      }

      if (
        state.mediaSample !== "none" &&
        state.headerFile instanceof File
      ) {
        formData.append("headerFile", state.headerFile);
      }

      const response = await createTemplate(formData);

      if (response.data?.success) {
        toast.success("Template submitted for approval!");
        navigate("/templates");
      } else {
        const error = response.data?.error || "Failed to create template";
        const details = response.data?.details;
        const fullMsg = details ? `${error} — ${details}` : error;
        setSubmitError(fullMsg);
        toast.error(error);
      }
    } catch (err) {
      const error =
        err.response?.data?.error || err.message || "Failed to create template";
      const details = err.response?.data?.details;
      const fullMsg = details ? `${error} — ${details}` : error;
      setSubmitError(fullMsg);
      toast.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  /* ─────── STEP 1: SET UP ─────── */
  if (step === 1) {
    const preview = SETUP_CATEGORY_PREVIEWS[state.category];

    return (
      <div className="tc-page">
        <div className="page-head tc-head">
          <button
            type="button"
            onClick={() => navigate("/templates")}
            className="camp-ico"
            title="Go back"
            aria-label="Go back"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h2>Create template</h2>
            <p>Set up category and message type</p>
          </div>
          <div className="grow" />
          <button
            type="button"
            onClick={() => navigate("/templates")}
            className="ping-btn ping-btn-ghost ping-btn-sm"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={() => setStep(2)}
            className="ping-btn ping-btn-green ping-btn-sm"
          >
            Next
          </button>
        </div>

        <TemplateCreatorStepBar current={1} />

        <div className="tc-grid">
          <div className="space-y-4 min-w-0">
            <SetupTemplateForm
              category={state.category}
              subtype={state.subtype}
              onCategoryChange={(value) => update("category", value)}
              onSubtypeChange={(value) => update("subtype", value)}
            />
            <PreApprovedTemplatesBanner />
          </div>

          <div className="tc-side">
            <SetupTemplatePreview
              category={state.category}
              preview={preview}
            />
          </div>
        </div>
      </div>
    );
  }

  /* ─────── STEP 2: EDIT ─────── */
  const isAuth = state.category === "AUTHENTICATION";
  const langDisplay = getLanguageDisplay(state.language);
  const subtypeLabel = getSubtypeLabel(state.category, state.subtype);

  return (
    <div className="tc-page">
      <div className="page-head tc-head">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="camp-ico"
          title="Go back"
          aria-label="Go back"
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <h2>Create template</h2>
          <p>Edit content and submit for review</p>
        </div>
        <div className="grow" />
        <button
          type="button"
          onClick={() => navigate("/templates")}
          className="ping-btn ping-btn-ghost ping-btn-sm"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="ping-btn ping-btn-green ping-btn-sm"
        >
          {submitting ? <Loader size={14} className="animate-spin" /> : null}
          {submitting ? "Submitting…" : "Submit for Review"}
        </button>
      </div>
      <div>
        <TemplateCreatorStepBar current={2} />

        <TemplateSummaryBanner
          name={state.name}
          languageLabel={langDisplay}
          category={state.category}
          subtypeLabel={subtypeLabel}
        />

        {submitError && (
          <div
            role="alert"
            className={`mt-4 flex items-start gap-2.5 border border-red-200 bg-red-50 px-4 py-3 ${TC_RADIUS}`}
          >
            <AlertCircle
              size={16}
              className="text-red-500 shrink-0 mt-0.5"
            />
            <p className="flex-1 text-xs text-red-700 leading-relaxed whitespace-pre-wrap">
              {submitError}
            </p>
            <button
              onClick={() => setSubmitError(null)}
              className="shrink-0 p-0.5 text-red-400 hover:text-red-600 rounded transition-colors text-sm leading-none"
            >
              ×
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4 mt-4">
          <div className="space-y-3">
            <div className={`border border-[#dddfe2] ${TC_RADIUS} p-4 bg-white`}>
              <h3 className="text-[15px] font-semibold text-[#050505] mb-3">
                Template name and language
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3 items-start">
                <div>
                  <label className="text-[13px] font-normal text-[#050505] block mb-1.5">
                    Name your template
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={state.name}
                      onChange={(e) =>
                        update("name", e.target.value.toLowerCase())
                      }
                      placeholder="Enter a template name"
                      className={`w-full pl-3 pr-14 py-2 text-[15px] border focus:outline-none transition-colors bg-white ${TC_RADIUS} ${
                        errors.name
                          ? "border-red-300"
                          : "border-[#ccd0d5] focus:border-[#1877f2]"
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-[#65676b] tabular-nums">
                      {state.name.length}/512
                    </span>
                  </div>
                  <FormFieldError message={errors.name} />
                </div>
                <div>
                  <label className="text-[13px] font-normal text-[#050505] block mb-1.5">
                    Select language
                  </label>
                  <TemplateLanguageSelect
                    value={state.language}
                    onChange={(code) => update("language", code)}
                    size="meta"
                  />
                  <FormFieldError />
                </div>
              </div>
            </div>

            {isAuth ? (
              <div className={`border border-[#dddfe2] ${TC_RADIUS} p-4 bg-white`}>
                <AuthenticationForm
                  codeDelivery={state.codeDelivery}
                  addSecurityRecommendation={state.addSecurityRecommendation}
                  addExpiryTime={state.addExpiryTime}
                  expiryMinutes={state.expiryMinutes}
                  onChange={update}
                />
              </div>
            ) : (
              <>
                <div className={`border border-[#dddfe2] ${TC_RADIUS} p-4 bg-white`}>
                  <h3 className="text-[15px] font-semibold text-[#050505] mb-1">
                    Content
                  </h3>
                  <p className="text-[13px] text-[#65676b] mb-4 leading-relaxed">
                    Fill in the header, body and footer sections of your template.
                    Cloud API hosted by Meta will review the template variables
                    and content to protect the security and integrity of our
                    services.{" "}
                    <a
                      href="https://developers.facebook.com/docs/whatsapp/business-management-api/message-templates"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1877f2] hover:underline"
                    >
                      About templates
                    </a>
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-[13px] font-normal text-[#050505] mb-1.5 flex items-center gap-1">
                        Type of variable
                        <Info size={14} className="text-[#65676b]" />
                      </label>
                      <select
                        value={state.variableType}
                        onChange={(e) =>
                          update("variableType", e.target.value)
                        }
                        className={`w-full max-w-[280px] px-3 py-2 text-[15px] border border-[#ccd0d5] focus:outline-none focus:border-[#1877f2] bg-white text-[#050505] ${TC_RADIUS}`}
                      >
                        {VARIABLE_TYPES.map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[13px] font-normal text-[#050505] mb-1.5 block">
                        Media sample · Optional
                      </label>
                      <select
                        value={state.mediaSample}
                        onChange={(e) =>
                          update("mediaSample", e.target.value)
                        }
                        className={`w-full max-w-[280px] px-3 py-2 text-[15px] border border-[#ccd0d5] focus:outline-none focus:border-[#1877f2] bg-white text-[#050505] ${TC_RADIUS}`}
                      >
                        {MEDIA_SAMPLES.map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-[13px] font-semibold text-[#050505] mb-1.5 block">
                        Header · Optional
                      </label>
                      <HeaderEditor
                        mediaSample={state.mediaSample}
                        headerText={state.headerText}
                        headerVariables={state.headerVariables}
                        variableType={state.variableType}
                        languageDisplay={langDisplay}
                        onHeaderTextChange={(v) => update("headerText", v)}
                        onHeaderVariablesChange={(v) =>
                          update("headerVariables", v)
                        }
                        headerFile={state.headerFile}
                        headerFilePreview={state.headerFilePreview}
                        onFileUpload={handleFileUpload}
                        onFileRemove={handleFileRemove}
                      />
                    </div>

                    <BodyEditor
                      body={state.body}
                      variableType={state.variableType}
                      bodyVariables={state.bodyVariables}
                      onBodyChange={(v) => update("body", v)}
                      onVariablesChange={(v) => update("bodyVariables", v)}
                    />

                    <FooterEditor
                      footer={state.footer}
                      onFooterChange={(v) => update("footer", v)}
                    />
                  </div>
                </div>

                <div className={`border border-[#dddfe2] ${TC_RADIUS} p-4 bg-white`}>
                  <ButtonsBuilder
                    ctaButtons={state.ctaButtons}
                    quickReplyButtons={state.quickReplyButtons}
                    onCtaChange={(b) => update("ctaButtons", b)}
                    onQrChange={(b) => update("quickReplyButtons", b)}
                    flows={[]}
                    variableType={state.variableType}
                  />
                </div>
              </>
            )}

          </div>

          <div className="lg:sticky lg:top-5 lg:self-start">
            <TemplatePreview state={state} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateCreator;
